import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useFormikContext } from 'formik';
import { defineMessages, useIntl } from 'react-intl';
import { ScreenReaderAriaMessage } from '@m/alchemy-ui/ScreenReaderAriaMessage';
import { type DragSourceData } from '../common/panel-dnd';
import { createPanel } from '../common/panel-factory';
import { deletePanel, type PanelConfigPatch, updatePanelConfig, updatePanelSize } from '../common/panel-grid';
import {
  getPanelDragData,
  getPanelPlacements,
  type PanelPlacement,
  resolvePanelPlacement
} from '../common/panel-placement';
import {
  type PageCmsPanel,
  type PageCmsPanels,
  type PanelConfigTypename,
  type PanelSize,
  panelTypesByTypename
} from '../common/panel-types';
import { PanelGrid } from '../CustomPage.styled';
import { usePanelDragMonitor } from '../hooks/usePanelDragMonitor';
import { PanelGridRow } from './PanelGridRow';
import { PanelGridRowDropZone } from './PanelGridRowDropZone';
import { PanelPlacementDialog } from './PanelPlacementDialog';

const messages = defineMessages({
  moved: {
    id: 'editCustomPagePanelMoved',
    defaultMessage: '{panel} moved to position {position} of {total} in row {row}.'
  },
  movedNewRow: {
    id: 'editCustomPagePanelMovedNewRow',
    defaultMessage: '{panel} moved to new row {row}.'
  },
  added: {
    id: 'editCustomPagePanelAdded',
    defaultMessage: '{panel} added at position {position} of {total} in row {row}.'
  },
  addedNewRow: {
    id: 'editCustomPagePanelAddedNewRow',
    defaultMessage: '{panel} added as new row {row}.'
  }
});

interface PanelGridFormValues {
  panels: PageCmsPanels;
}

interface PanelGridLayoutProps {
  readonly showPanelErrors: boolean;
}

export interface PanelGridLayoutHandle {
  openNewPanelPlacement: (typename: PanelConfigTypename, trigger: HTMLElement) => void;
}

interface PanelPlacementRequest {
  mode: 'move' | 'add';
  source: DragSourceData;
  incomingPanel: PageCmsPanel;
  panelName: string;
  panels: PageCmsPanels;
  placements: PanelPlacement[];
  returnFocusTo: HTMLElement;
}

export const PanelGridLayout = forwardRef<PanelGridLayoutHandle, PanelGridLayoutProps>(({ showPanelErrors }, ref) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<PanelGridFormValues>();
  const { panels } = values;
  const gridRef = useRef<HTMLDivElement>(null);
  const [placementRequest, setPlacementRequest] = useState<PanelPlacementRequest | null>(null);
  const [placementAnnouncement, setPlacementAnnouncement] = useState('');
  const pendingPanelFocusRef = useRef<{ panelId: string; panels: PageCmsPanels } | null>(null);
  const pendingReturnFocusRef = useRef<HTMLElement | null>(null);

  // The callbacks read the grid through a ref so they stay referentially stable: editing one panel
  // then re-renders only its own memoized row.
  const panelsRef = useRef(panels);
  panelsRef.current = panels;

  const handleSizeChange = useCallback(
    (rowIndex: number, panelId: string, size: PanelSize) =>
      setFieldValue('panels', updatePanelSize(panelsRef.current, rowIndex, panelId, size)),
    [setFieldValue]
  );

  const handleDeletePanel = useCallback(
    (rowIndex: number, panelId: string) => setFieldValue('panels', deletePanel(panelsRef.current, rowIndex, panelId)),
    [setFieldValue]
  );

  const handleConfigChange = useCallback(
    (rowIndex: number, panelId: string, patch: PanelConfigPatch) =>
      setFieldValue('panels', updatePanelConfig(panelsRef.current, rowIndex, panelId, patch)),
    [setFieldValue]
  );

  const handleDrop = useCallback((next: PageCmsPanels) => setFieldValue('panels', next), [setFieldValue]);
  const { isDragActive, dropPreview } = usePanelDragMonitor(panels, handleDrop);

  const openPlacement = useCallback(
    (
      mode: PanelPlacementRequest['mode'],
      source: DragSourceData,
      incomingPanel: PageCmsPanel,
      returnFocusTo: HTMLElement
    ) => {
      const currentPanels = panelsRef.current;
      const panelName = intl.formatMessage(panelTypesByTypename[incomingPanel.settings.__typename].message);

      setPlacementAnnouncement('');
      setPlacementRequest({
        mode,
        source,
        incomingPanel,
        panelName,
        panels: currentPanels,
        placements: getPanelPlacements(currentPanels, source, incomingPanel),
        returnFocusTo
      });
    },
    [intl]
  );

  const handleOpenPanelPlacement = useCallback(
    (panelId: string, trigger: HTMLElement) => {
      const currentPanels = panelsRef.current;
      const source = getPanelDragData(currentPanels, panelId);

      if (source) {
        openPlacement('move', source, currentPanels[source.rowIndex][source.panelIndex], trigger);
      }
    },
    [openPlacement]
  );

  const openNewPanelPlacement = useCallback(
    (typename: PanelConfigTypename, trigger: HTMLElement) => {
      const panel = createPanel(typename);
      openPlacement('add', { type: 'new-panel', typename }, panel, trigger);
    },
    [openPlacement]
  );

  const handlePlacementCancel = useCallback(() => {
    if (placementRequest) {
      pendingReturnFocusRef.current = placementRequest.returnFocusTo;
      setPlacementRequest(null);
    }
  }, [placementRequest]);

  const handlePlacementConfirm = useCallback(
    ({ position, location }: PanelPlacement) => {
      if (!placementRequest || placementRequest.panels !== panelsRef.current) {
        handlePlacementCancel();
        return;
      }

      const { incomingPanel, mode, panelName, panels: placementPanels, source } = placementRequest;
      const next = resolvePanelPlacement(placementPanels, source, position, incomingPanel);

      if (next === placementPanels) {
        handlePlacementCancel();
        return;
      }

      pendingPanelFocusRef.current = { panelId: incomingPanel.id, panels: next };
      setPlacementRequest(null);
      handleDrop(next);
      setPlacementAnnouncement(
        intl.formatMessage(
          location.isNewRow
            ? mode === 'move'
              ? messages.movedNewRow
              : messages.addedNewRow
            : mode === 'move'
              ? messages.moved
              : messages.added,
          {
            panel: panelName,
            row: location.rowIndex + 1,
            position: location.panelIndex + 1,
            total: location.rowLength
          }
        )
      );
    },
    [handleDrop, handlePlacementCancel, intl, placementRequest]
  );

  useImperativeHandle(ref, () => ({ openNewPanelPlacement }), [openNewPanelPlacement]);

  useEffect(() => {
    if (placementRequest && placementRequest.panels !== panels) {
      pendingReturnFocusRef.current = placementRequest.returnFocusTo;
      setPlacementRequest(null);
    }
  }, [panels, placementRequest]);

  useLayoutEffect(() => {
    const pendingPanelFocus = pendingPanelFocusRef.current;

    if (pendingPanelFocus?.panels === panels) {
      pendingPanelFocusRef.current = null;
      Array.from(gridRef.current?.querySelectorAll<HTMLElement>('[data-panel-options-button]') ?? [])
        .find((button) => button.dataset.panelOptionsButton === pendingPanelFocus.panelId)
        ?.focus();
    }

    if (!placementRequest && pendingReturnFocusRef.current) {
      const returnFocusTo = pendingReturnFocusRef.current;
      pendingReturnFocusRef.current = null;
      const fallback = gridRef.current?.querySelector<HTMLElement>('[data-panel-options-button]');
      (returnFocusTo.isConnected ? returnFocusTo : fallback)?.focus();
    }
  }, [panels, placementRequest]);

  return (
    <PanelGrid ref={gridRef} data-testid="panel-grid-layout">
      <ScreenReaderAriaMessage
        message={placementAnnouncement}
        aria-live="polite"
        aria-atomic="true"
        data-testid="panel-grid-announcement"
      />
      <PanelGridRowDropZone rowIndex={0} isDragActive={isDragActive} />
      {panels.map((row, rowIndex) => {
        return (
          // Rows have no id of their own; keying by panel ids remounts a row exactly when its membership changes.
          <Fragment key={row.map((panel) => panel.id).join(':')}>
            <PanelGridRow
              row={row}
              rowIndex={rowIndex}
              onSizeChange={handleSizeChange}
              onDeletePanel={handleDeletePanel}
              onMovePanel={handleOpenPanelPlacement}
              onConfigChange={handleConfigChange}
              showPanelErrors={showPanelErrors}
              dropPreview={dropPreview?.rowIndex === rowIndex ? dropPreview : null}
            />
            <PanelGridRowDropZone rowIndex={rowIndex + 1} isDragActive={isDragActive} />
          </Fragment>
        );
      })}
      {placementRequest && (
        <PanelPlacementDialog
          mode={placementRequest.mode}
          panelName={placementRequest.panelName}
          rowCount={placementRequest.panels.length}
          placements={placementRequest.placements}
          onCancel={handlePlacementCancel}
          onConfirm={handlePlacementConfirm}
        />
      )}
    </PanelGrid>
  );
});

PanelGridLayout.displayName = 'PanelGridLayout';
